import React from "react";
import { useNavigate } from "react-router-dom";

// components
import logo from "../../../assets/images/icons/green-logo.png";
import login from "../../../assets/images/icons/login.png";
import ButtonLink from "../../../components/ui/links/ButtonLink";

// constants
import { IoMenuOutline } from "react-icons/io5";

// state and dispatch
import { get_profile } from "../../../service/api/auth";
import { useAppDispatch, useAppSelector } from "../../../store";
import NavBarItem from "./NavBarItem";
import MobileMenu from "../menu/MobileMenu";
import { IUser } from "../../../store/@types/user";

type isHidden = {
  isHidden: boolean;
};

const Navbar: React.FC<isHidden> = ({ isHidden = false }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(({ auth }) => auth.user);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState<boolean>(false);

  const onLoad = React.useCallback(() => {
    get_profile(dispatch);
  }, [dispatch]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  // Prevent body scroll when mobile menu is open
  React.useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-4 ${
          isHidden ? "hidden" : "block"
        }`}
      >
        <div className="max-w-7xl mx-auto bg-white/60 backdrop-blur-md rounded-full shadow-lg px-4 sm:px-6 py-3 flex justify-between items-center">
          {/* Logo */}
          <div className="company-logo">
            <div className="h-[3.125rem]">
              <img
                loading="eager"
                decoding="async"
                src={logo}
                className="h-full object-contain cursor-pointer"
                alt="icon"
                onClick={() => navigate("/")}
              />
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <NavBarItem open={false} onClick={() => {}} />
          </div>

          {/* Right Section - User/Auth & Mobile Menu Toggle */}
          <div className="flex items-center gap-3">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-1 rounded-lg transition-colors lg:hidden"
              aria-label="Open menu"
            >
              <IoMenuOutline size={32} className="text-gray-700" />
            </button>

            <div className="flex items-center gap-3">
              {user ? (
                <div className="flex items-center justify-end">
                  <img
                    src={
                      user?.profile?.avatar || "https://via.placeholder.com/50"
                    }
                    alt={user?.profile?.name || "User"}
                    onClick={() => navigate("/dashboard")}
                    className="h-[3.125rem] w-[3.125rem] rounded-full border-2 border-primary-500 object-cover cursor-pointer hover:scale-105 transition-transform duration-200"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "https://via.placeholder.com/50";
                    }}
                  />
                </div>
              ) : (
                <>
                  {/* Desktop Auth Buttons */}
                  <div className="hidden lg:flex lg:items-center gap-2">
                    <ButtonLink
                      link="/login"
                      className="group relative text-primary-900 hover:text-white border border-primary-900 hover:border-primary-900 transition ease-in-out duration-300 overflow-hidden"
                    >
                      <span className="absolute inset-0 w-full h-full transition-all duration-300 ease-out transform translate-x-full bg-primary-900 group-hover:translate-x-0"></span>
                      <span className="relative z-10">Login</span>
                    </ButtonLink>
                    <ButtonLink
                      link="/register"
                      className="group relative text-white ml-2 bg-primary-900 text-md font-medium border border-primary-900 hover:bg-primary-700 transition ease-in-out duration-300 overflow-hidden"
                    >
                      <span className="absolute inset-0 w-full h-full transition-all duration-300 ease-out transform -translate-x-full bg-primary-700 group-hover:translate-x-0"></span>
                      <span className="relative z-10">Sign Up</span>
                    </ButtonLink>
                  </div>

                  {/* Mobile Login Icon */}
                  <div className="lg:hidden">
                    <img
                      src={login}
                      className="w-6 h-6 cursor-pointer"
                      alt="login logo"
                      onClick={() => navigate("/login")}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Component */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        user={user as IUser}
      />
    </>
  );
};

export default Navbar;
